import React, { useMemo, useState } from 'react';
import { StatCard } from '../components/base';
import { fmt } from '../lib/formato';
import { montarLista, resumo, semContato } from '../lib/contatos';
import { STAGES } from '../lib/constantes';
import { baixarCSV } from '../lib/exportar';

const FILTROS = [
  { key: 'todos',       label: 'Todos' },
  { key: 'ativos',      label: 'Com negócio aberto' },
  { key: 'clientes',    label: 'Clientes' },
  { key: 'multiplos',   label: 'Mais de um negócio' },
  { key: 'incompletos', label: 'Sem e-mail nem telefone' },
];

function ContatosView({ contatos = [], leads = [], onOpenLead }) {
  const [busca, setBusca]   = useState('');
  const [filtro, setFiltro] = useState('todos');
  const [aberto, setAberto] = useState(null);

  const nums  = useMemo(() => resumo(contatos, leads), [contatos, leads]);
  const lista = useMemo(() => montarLista(contatos, leads, { busca, filtro }), [contatos, leads, busca, filtro]);
  const orfaos = useMemo(() => semContato(leads), [leads]);

  const etapaLabel = (k) => STAGES.find(s => s.key === k)?.label || k || 'Backlog';
  const detalhe = aberto ? lista.find(c => c.id === aberto) || null : null;

  const exportar = () => baixarCSV('contatos', lista.map(c => ({
    Nome: c.nome, Empresa: c.empresa || '', 'E-mail': c.email || '', Telefone: c.telefone || '',
    Negociacoes: c.total, Abertas: c.ativos.length, Ganhas: c.ganhos.length, Perdidas: c.perdidos.length,
    Pipeline: c.pipeline, Receita: c.receita, 'Receita contrato': c.receitaContrato,
    'Win rate %': c.winRate === null ? '' : c.winRate.toFixed(0),
    Incompleto: c.incompleto ? 'sim' : 'nao',
  })));

  return <div className="flex flex-col gap-5">
    <div className="flex items-center justify-between gap-3 flex-wrap">
      <h2 className="text-lg font-bold text-gray-800">Contatos</h2>
      <button onClick={exportar}
        className="flex items-center gap-1.5 text-sm px-3 py-2 rounded-xl border border-gray-200 bg-white text-gray-600 hover:border-gray-300 transition-colors">
        <span aria-hidden="true">⬇</span> CSV ({lista.length})
      </button>
    </div>

    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
      <StatCard label="Contatos"        value={nums.total} />
      <StatCard label="Clientes"        value={nums.clientes} color="text-green-600" sub="já fecharam ao menos uma vez" />
      <StatCard label="Com negócio aberto" value={nums.comAtivo} color="text-blue-600" />
      <StatCard label="Mais de um negócio" value={nums.multiplos} color="text-purple-600" />
      <StatCard label="Pipeline"        value={fmt(nums.pipeline)} color="text-blue-700" />
      <StatCard label="Receita"         value={fmt(nums.receita)} color="text-green-700" />
    </div>

    {orfaos.length > 0 && (
      <div className="acard rounded-2xl px-4 py-3 flex items-center gap-3 flex-wrap border-amber-200 bg-amber-50">
        <span aria-hidden="true">⚠️</span>
        <span className="text-sm text-amber-800">
          {orfaos.length} negociação(ões) sem contato vinculado. Entraram depois da migração.
        </span>
      </div>
    )}

    <div className="flex gap-2 flex-wrap items-center">
      <input
        className="apple-input flex-1 min-w-56 px-3 py-2 text-sm"
        placeholder="🔍 Buscar por nome, empresa, e-mail ou telefone..."
        value={busca} onChange={e => setBusca(e.target.value)}
      />
      <div className="flex gap-0.5 bg-black/[0.05] rounded-full p-0.5 flex-wrap">
        {FILTROS.map(f => (
          <button key={f.key} onClick={() => setFiltro(f.key)}
            className={`text-xs px-3 py-1.5 rounded-full font-medium transition-all whitespace-nowrap ${
              filtro === f.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}>
            {f.label}
          </button>
        ))}
      </div>
    </div>

    {lista.length === 0 ? (
      <p className="text-sm text-gray-400 text-center py-12">
        {contatos.length === 0
          ? 'Nenhum contato ainda. Eles são criados junto com as negociações.'
          : 'Nada encontrado com esse filtro.'}
      </p>
    ) : (
      <div className="acard rounded-2xl divide-y divide-black/[0.05]">
        {lista.map(c => {
          const expandido = aberto === c.id;
          return (
            <div key={c.id}>
              <button onClick={() => setAberto(expandido ? null : c.id)}
                aria-expanded={expandido}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-black/[0.02] transition-colors text-left">
                <div className="w-9 h-9 rounded-full bg-blue-100 text-blue-700 font-bold text-xs flex items-center justify-center flex-shrink-0">
                  {(c.nome || '?').slice(0, 2).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">
                    {c.nome}
                    {c.ehCliente && <span className="ml-2 t-caption-2 bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-medium">cliente</span>}
                    {c.incompleto && <span className="ml-2 t-caption-2 bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-medium">sem contato</span>}
                  </p>
                  <p className="t-caption-2 text-gray-400 truncate">
                    {[c.empresa, c.email, c.telefone].filter(Boolean).join(' · ') || 'sem dados de contato'}
                  </p>
                </div>
                <div className="hidden sm:flex items-center gap-4 flex-shrink-0 text-right">
                  <div>
                    <p className="t-caption-2 text-gray-400">Negócios</p>
                    <p className="text-sm font-semibold text-gray-700">{c.total}</p>
                  </div>
                  {c.pipeline > 0 && <div>
                    <p className="t-caption-2 text-gray-400">Pipeline</p>
                    <p className="text-sm font-semibold text-blue-700">{fmt(c.pipeline)}</p>
                  </div>}
                  {c.receita > 0 && <div>
                    <p className="t-caption-2 text-gray-400">Receita</p>
                    <p className="text-sm font-semibold text-green-700">{fmt(c.receita)}</p>
                  </div>}
                </div>
                <span aria-hidden="true" className={`text-gray-300 transition-transform ${expandido ? 'rotate-90' : ''}`}>›</span>
              </button>

              {expandido && detalhe && (
                <div className="px-4 pb-4 bg-black/[0.015]">
                  <div className="flex flex-wrap gap-4 py-3 t-caption-2 text-gray-500">
                    {detalhe.winRate !== null && <span>Win rate <strong className="text-gray-700">{detalhe.winRate.toFixed(0)}%</strong></span>}
                    {detalhe.receitaContrato > detalhe.receita && <span>Contrato total <strong className="text-green-700">{fmt(detalhe.receitaContrato)}</strong></span>}
                    {detalhe.perdido > 0 && <span>Perdido <strong className="text-red-500">{fmt(detalhe.perdido)}</strong></span>}
                    {detalhe.ultimaAtividade && <span>Última atividade em {new Date(detalhe.ultimaAtividade).toLocaleDateString('pt-BR')}</span>}
                  </div>
                  <div className="space-y-1.5">
                    {detalhe.leads.map(l => {
                      const ganho = l.stage === 'fechado', perda = l.stage === 'perdido';
                      return (
                        <div key={l.id} onClick={() => onOpenLead && onOpenLead(l)}
                          className="flex items-center justify-between gap-3 bg-white border border-black/[0.05] rounded-xl px-3 py-2 cursor-pointer hover:border-black/[0.12] transition-colors">
                          <span className="text-sm text-gray-800 truncate flex-1">{l.company_name}</span>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {l.value > 0 && <span className={`text-xs font-bold ${ganho ? 'text-green-700' : 'text-gray-600'}`}>{fmt(Number(l.value))}</span>}
                            <span className={`t-caption-2 px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${
                              ganho ? 'bg-green-100 text-green-700' : perda ? 'bg-red-100 text-red-600' : 'bg-blue-50 text-blue-600'}`}>
                              {etapaLabel(l.stage)}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    )}
  </div>;
}

export { ContatosView };
